import * as accountAPI from "api/@auth/api_auth";
import produce from "immer";
import { createAction, handleActions } from "redux-actions";
import { select, takeLatest } from "redux-saga/effects";
import store from "store";
import { localStorageKeyLastSignInEmail, localStorageKeyToken } from "utils/(localStorage)";
import { handleDropout } from "../../../utils/nativeApi";
import createRequestSaga, { createRequestActionTypes } from "../createRequestSaga";

// Action Type 정의
const INIT_FIELD = "account/INIT_FIELD";
const CHANGE_FIELD = "account/CHANGE_FIELD";
const INITIALIZE_FORM = "account/INITIALIZE_FORM";
const REFRESH_ACCESS_TOKEN = "account/REFRESH_ACCESS_TOKEN";
const AUTO_LOGIN = "account/AUTO_LOGIN";
const RESEND_TEL = "account/RESEND_TEL";
const SELECT_COUNTRY = "account/SELECT_COUNTRY";
const CREATE_TEMPORARY_ID = "account/CREATE_TEMPORARY_ID";
const SET_LOGIN_BOTTOMSHEET = "account/SET_LOGIN_BOTTOMSHEET";

const [CHECK_EMAIL, CHECK_EMAIL_SUCCESS, CHECK_EMAIL_FAILURE] = createRequestActionTypes("account/CHECK_EMAIL");

const [REGISTER, REGISTER_SUCCESS, REGISTER_FAILURE] = createRequestActionTypes("account/REGISTER");

const [REGISTER_KB, REGISTER_KB_SUCCESS, REGISTER_KB_FAILURE] = createRequestActionTypes("account/KB_REGISTER");

const [LOGIN, LOGIN_SUCCESS, LOGIN_FAILURE] = createRequestActionTypes("account/LOGIN");
const LOGOUT = "account/LOGOUT";
const [DROPOUT, DROPOUT_SUCCESS, DROPOUT_FAILURE] = createRequestActionTypes("account/DROPOUT");
const [CHECK_FORGOT, CHECK_FORGOT_SUCCESS, CHECK_FORGOT_FAILURE] = createRequestActionTypes("account/CHECK_FORGOT");
const [VERIFY_FORGOT, VERIFY_FORGOT_SUCCESS, VERIFY_FORGOT_FAILURE] = createRequestActionTypes("account/VERIFY_FORGOT");
const [CHANGE_FORGOT, CHANGE_FORGOT_SUCCESS, CHANGE_FORGOT_FAILURE] = createRequestActionTypes("account/CHANGE_FORGOT");
const [CHANGE_PASSWORD, CHANGE_PASSWORD_SUCCESS, CHANGE_PASSWORD_FAILURE] =
  createRequestActionTypes("account/CHANGE_PASSWORD");
const INIT_VERIFY_FORGOT = "account/INIT_VERIFY_FORGOT";
const [GET_ACCOUNT, GET_ACCOUNT_SUCCESS, GET_ACCOUNT_FAILURE] = createRequestActionTypes("account/GET_ACCOUNT");

// Action 생성
export const initField = createAction(INIT_FIELD, ({ form, key }) => ({
  form,
  key,
}));
export const changeField = createAction(CHANGE_FIELD, ({ form, key, value }) => ({
  form, // register , login, forgot
  key, // email, password, passwordConfirm, ...
  value, // 실제 바꾸려는 값
}));
export const initializeForm = createAction(INITIALIZE_FORM, (form) => form); // register / login / forgot

export const setActiveLoginBottomSheet = createAction(
  SET_LOGIN_BOTTOMSHEET,
  (isActiveLoginBottomSheet) => isActiveLoginBottomSheet,
);

export const getAccountAction = createAction(GET_ACCOUNT, (account) => account);

/** 로그인 성공 시  */
function* handleLoginSuccess() {
  const account = yield select((state) => state.account.account);
  accountAPI.setAuthorization(account.access_token);
  store.set(localStorageKeyToken, { refreshToken: account.refresh_token, accessToken: account.access_token });
  store.set(localStorageKeyLastSignInEmail, account.email);
  sessionStorage.clear();
  console.log("%c로그인에 성공했습니다.", "color: #6435c7; font-weight: bold");
}

function handleDropoutSuccess() {
  handleDropout();
  logout();
}

export const checkEmail = createAction(CHECK_EMAIL, (email) => email);
export const register = createAction(REGISTER, ({ email, password }) => ({
  email,
  password,
}));

export const selectCountry = createAction(SELECT_COUNTRY);
export const login = createAction(LOGIN, ({ email, password }) => ({
  email,
  password,
}));

export const autoLogin = createAction(AUTO_LOGIN, (token) => token);
export const logout = createAction(LOGOUT, accountAPI.logout);
export const dropout = createAction(DROPOUT);

export const resendTel = createAction(RESEND_TEL);
export const createTemporaryId = createAction(CREATE_TEMPORARY_ID);

// saga 생성
export const checkEmailSaga = createRequestSaga(CHECK_EMAIL, accountAPI.checkEmail);

const registerSaga = createRequestSaga(REGISTER, accountAPI.register);
const loginSaga = createRequestSaga(LOGIN, accountAPI.login);
const dropoutSaga = createRequestSaga(DROPOUT, accountAPI.dropout);
const getAccountSaga = createRequestSaga(GET_ACCOUNT, accountAPI.getAccountInfo);

export function* accountSaga() {
  yield takeLatest(GET_ACCOUNT, getAccountSaga);
  yield takeLatest(CHECK_EMAIL, checkEmailSaga);
  yield takeLatest(REGISTER, registerSaga);
  yield takeLatest(LOGIN, loginSaga);
  yield takeLatest(DROPOUT, dropoutSaga);
  yield takeLatest(DROPOUT_SUCCESS, handleDropoutSuccess);
  yield takeLatest(LOGIN_SUCCESS, handleLoginSuccess);
}
const initialState = {
  register: {
    email: "",
    duplicated: null,
    password: "",
    passwordConfirm: "",
    passwordShow: false,
    passwordConfirmShow: false,
    tel: "",
    telConfirm: "",
    telConfirmResult: false,
    error: null,
    country: "",
  },
  login: {
    email: "",
    password: "",
    passwordShow: false,
    temporary_id: "",
    autoLogin: true,
    isActiveLoginBottomSheet: false,
  },
  change: {
    email: "",
    password: "",
    newPassword: "",
    newPasswordConfirm: "",
    passwordChanged: null,
    error: null,
  },
  forgot: {
    email: "",
    verificationCode: "",
    newPassword: "",
    newPasswordConfirm: "",
    emailChecked: null,
    codeVerified: null,
    passwordChanged: null,
    cause: null,
    forgotId: null,
    error: null,
  },
  account: null,
  error: null,
};

const account = handleActions(
  {
    [INIT_FIELD]: (state, { payload: { form, key } }) =>
      produce(state, (draft) => {
        draft[form][key] = initialState[form][key]; // 예: state.form.ooo 초기 상태로 바꾼다
      }),
    [GET_ACCOUNT_SUCCESS]: (state, { payload: account }) => ({
      ...state,
      account: account?.account,
    }),
    [GET_ACCOUNT_FAILURE]: (state, { payload: error }) => ({
      ...state,
      account: null,
      checkError: error,
    }),
    [CHANGE_FIELD]: (state, { payload: { form, key, value } }) =>
      produce(state, (draft) => {
        draft[form][key] = value; // 예: state.register.username을 바꾼다
      }),
    [INITIALIZE_FORM]: (state, { payload: form }) => ({
      ...state,
      [form]: initialState[form],
      error: null, // 폼 전환 시 회원 인증 에러 초기화
    }),
    [REFRESH_ACCESS_TOKEN]: (state, { payload: newToken }) => ({
      ...state,
      account: { ...state.account, access_token: newToken },
    }),
    [SET_LOGIN_BOTTOMSHEET]: (state, { payload: isActiveLoginBottomSheet }) => ({
      ...state,
      login: { ...state.login, isActiveLoginBottomSheet },
    }),

    [AUTO_LOGIN]: (state, { payload: account }) => ({
      ...state,
      account: account,
    }),
    // 이메일 중복검사 성공 (available 결과 값 -> true/false)
    [CHECK_EMAIL_SUCCESS]: (state, { payload: res }) => ({
      ...state,
      register: {
        ...state.register,
        duplicated: res.duplicated,
        with: res.with,
      },
    }),
    // 이메일 중복검사 실패
    [CHECK_EMAIL_FAILURE]: (state, { payload: error }) => ({
      ...state,
      error: error,
    }),

    // KB 회원가입 성공
    [REGISTER_KB_SUCCESS]: (state, { payload: account }) => ({
      ...state,
      error: null,
      account: account.result,
    }),
    // KB 회원가입 실패
    [REGISTER_KB_FAILURE]: (state, { payload: error }) => ({
      ...state,
      error: error,
    }),

    // 회원가입 성공
    [REGISTER_SUCCESS]: (state, { payload: account }) => ({
      ...state,
      error: null,
      account,
    }),
    // 회원가입 실패
    [REGISTER_FAILURE]: (state, { payload: error }) => ({
      ...state,
      error: error,
    }),

    // 로그인 시도
    [LOGIN]: (state) => {
      return {
        ...state,
        error: null,
      };
    },

    // 로그인 성공
    [LOGIN_SUCCESS]: (state, { payload: account }) => {
      return {
        ...state,
        error: null,
        login: { ...state.login, password: "" },
        account: account.result,
      };
    },

    // 로그인 실패
    [LOGIN_FAILURE]: (state, { payload: error }) => ({
      ...state,
      error: error.response.data,
    }),

    // 로그아웃
    [LOGOUT]: (state) => ({
      ...state,
      login: {
        ...state.login,
        email: localStorage.getItem("email") || "",
        password: "",
      },
    }),
    // 비밀번호 분실 시 이메일 인증 성공
    [CHECK_FORGOT_SUCCESS]: (state, { payload: result }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        emailChecked: result.success,
        cause: result.cause,
        forgotId: result.forgot_id,
        error: null,
      },
    }),
    // 비밀번호 분실 시 이메일 인증 실패
    [CHECK_FORGOT_FAILURE]: (state, { payload: error }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        error: error.response.data,
      },
    }),
    // 비밀번호 분실 시 이메일 인증코드 확인 성공
    [VERIFY_FORGOT_SUCCESS]: (state, { payload: result }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        error: null,
        codeVerified: true,
      },
    }),
    // 비밀번호 분실 시 이메일 인증코드 확인 실패
    [VERIFY_FORGOT_FAILURE]: (state, { payload: error }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        error: error.response.data,
      },
    }),
    [INIT_VERIFY_FORGOT]: (state) => ({
      ...state,
      forgot: {
        ...state.forgot,
        codeVerified: initialState.forgot.codeVerified,
      },
    }),
    // 비밀번호 분실 시 비밀번호 재설정 성공
    [CHANGE_FORGOT_SUCCESS]: (state, { payload: result }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        passwordChanged: true,
        error: null,
      },
    }),
    // 비밀번호 분실 시 비밀번호 재설정 실패
    [CHANGE_FORGOT_FAILURE]: (state, { payload: error }) => ({
      ...state,
      forgot: {
        ...state.forgot,
        error: error,
      },
    }),

    // 비밀번호 변경 성공
    [CHANGE_PASSWORD_SUCCESS]: (state, { payload: result }) => ({
      ...state,
      change: {
        ...state.change,
        passwordChanged: true,
        error: null,
      },
    }),
    // 비밀번호 변경 실패
    [CHANGE_PASSWORD_FAILURE]: (state, { payload: error }) => ({
      ...state,
      change: {
        ...state.change,
        error: error.response.data,
      },
    }),
    [SELECT_COUNTRY]: (state, { payload: country }) => {
      return {
        ...state,
        register: {
          ...state.register,
          country,
        },
      };
    },
    [CREATE_TEMPORARY_ID]: (state, { payload: temporaryId }) => {
      return {
        ...state,
        login: {
          ...state.login,
          temporary_id: temporaryId,
        },
      };
    },
  },
  initialState,
);

export const getAccountId = (state) => state.account.account?.account_id;
export const getIsActiveLoginBottomSheet = (state) => state.account.login.isActiveLoginBottomSheet;
export const getCompanyInfo = (state) => state.account.account?.company;

export default account;
